import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import FormInput from "../../../components/Form/FormInput";
import FormTextarea from "../../../components/Form/TextArea";
import BackButton from "../../../components/Button/BackButton";
import { btn } from "../../../components/Button/buttonStyles";

const STATUS_BADGE = {
  Active: "bg-emerald-50 text-emerald-700",
  Inactive: "bg-gray-100 text-gray-600",
  Pending: "bg-amber-50 text-amber-700",
};

const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-gray-500">—</span>;
  const cls = STATUS_BADGE[status] || "bg-gray-100 text-gray-700";
  return (
    <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>
      {status}
    </span>
  );
};

const clientName = (c) =>
  c.company_name ||
  `${c.primary_user?.first_name || ""} ${c.primary_user?.last_name || ""}`.trim() ||
  "—";

const managerName = (m) =>
  `${m.first_name || ""} ${m.last_name || ""}`.trim() || `Manager #${m.id}`;

const managerContact = (m) => m.auth?.email || m.email || m.phone || "—";

const AddClientPoolPage = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    description: "",
    client_ids: [],
    manager_ids: [],
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [clients, setClients] = useState([]);
  const [managers, setManagers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientsRes, managersRes] = await Promise.all([
          api.get("/clients"),
          api.get("/managers"),
        ]);
        setClients(clientsRes.data.data || []);
        setManagers(managersRes.data.data || []);
      } catch (error) {
        console.error("Error fetching clients/managers:", error);
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setServerError("");
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === "name" && !value) {
      setErrors((prev) => ({ ...prev, name: "Pool Name is required." }));
    }
  };

  const handleMultiSelect = (name, id) => {
    setForm((prev) => {
      const selected = new Set(prev[name]);
      if (selected.has(id)) {
        selected.delete(id);
      } else {
        selected.add(id);
      }
      return { ...prev, [name]: [...selected] };
    });
  };

  const validate = () => {
    const currentErrors = {};
    if (!form.name) currentErrors.name = "Pool Name is required.";
    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setServerError("");
    try {
      await api.post("/client-pools", form);
      navigate("/client-pools");
    } catch (error) {
      console.error("Error creating client pool:", error);
      setServerError(
        error.response?.data?.message || "Failed to create client pool"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 mb-5">
        <BackButton />
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Create Client Pool</h1>
      </div>

      <form
        onSubmit={handleSubmit}
        noValidate
        className="bg-white shadow-sm border border-gray-100 rounded-lg p-5 space-y-6"
      >
        <section className="space-y-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Pool Info
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4 items-start">
            <FormInput
              label="Pool Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.name}
              required
            />
            <div />
            <div className="md:col-span-2">
              <FormTextarea
                label="Description"
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={3}
              />
            </div>
          </div>
        </section>

        <section className="space-y-3 pt-2 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Clients
            </h3>
            <span className="text-[10px] font-semibold text-gray-500">
              ({form.client_ids.length} selected)
            </span>
          </div>
          <div className="w-full bg-white shadow rounded-lg overflow-hidden border border-gray-100">
            <div className="max-h-[300px] overflow-y-auto overflow-x-auto">
              <table className="w-full table-auto border-collapse min-w-[700px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                    <th className="px-3 py-2 text-center font-semibold w-12">Select</th>
                    <th className="px-3 py-2 text-center font-semibold">Name</th>
                    <th className="px-3 py-2 text-center font-semibold">Type</th>
                    <th className="px-3 py-2 text-center font-semibold">Status</th>
                    <th className="px-3 py-2 text-center font-semibold">Country</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="py-10 text-center text-gray-400 text-xs"
                      >
                        No clients available.
                      </td>
                    </tr>
                  ) : (
                    clients.map((c, index) => {
                      const isChecked = form.client_ids.includes(c.id);
                      const rowBg = isChecked
                        ? "bg-brand-50"
                        : index % 2 === 0
                        ? "bg-gray-50"
                        : "bg-white";
                      return (
                        <tr
                          key={c.id}
                          onClick={() => handleMultiSelect("client_ids", c.id)}
                          className={`${rowBg} hover:bg-brand-50 transition-colors cursor-pointer`}
                        >
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleMultiSelect("client_ids", c.id)}
                              className="w-3.5 h-3.5 accent-brand-600 cursor-pointer"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-900">
                            {clientName(c)}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {c.type || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            <StatusBadge status={c.status} />
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {c.country || "—"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="space-y-3 pt-2 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Managers
            </h3>
            <span className="text-[10px] font-semibold text-gray-500">
              ({form.manager_ids.length} selected)
            </span>
          </div>
          <div className="w-full bg-white shadow rounded-lg overflow-hidden border border-gray-100">
            <div className="max-h-[300px] overflow-y-auto overflow-x-auto">
              <table className="w-full table-auto border-collapse min-w-[600px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                    <th className="px-3 py-2 text-center font-semibold w-12">Select</th>
                    <th className="px-3 py-2 text-center font-semibold">Name</th>
                    <th className="px-3 py-2 text-center font-semibold">Role</th>
                    <th className="px-3 py-2 text-center font-semibold">Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {managers.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="py-10 text-center text-gray-400 text-xs"
                      >
                        No managers available.
                      </td>
                    </tr>
                  ) : (
                    managers.map((m, index) => {
                      const isChecked = form.manager_ids.includes(m.id);
                      const rowBg = isChecked
                        ? "bg-brand-50"
                        : index % 2 === 0
                        ? "bg-gray-50"
                        : "bg-white";
                      return (
                        <tr
                          key={m.id}
                          onClick={() => handleMultiSelect("manager_ids", m.id)}
                          className={`${rowBg} hover:bg-brand-50 transition-colors cursor-pointer`}
                        >
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleMultiSelect("manager_ids", m.id)}
                              className="w-3.5 h-3.5 accent-brand-600 cursor-pointer"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-900">
                            {managerName(m)}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {m.role?.role_details?.name || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {managerContact(m)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {serverError && (
          <div className="bg-danger-50 border border-danger-200 text-danger-700 text-sm px-3 py-2 rounded">
            {serverError}
          </div>
        )}

        <div className="pt-3 border-t border-gray-100 space-y-3">
          <p className="text-xs text-gray-500">
            Fields marked with <span className="text-danger-500">*</span> are mandatory.
          </p>
          <div className="flex gap-2 text-sm">
            <button
              type="submit"
              disabled={saving}
              className={btn("primary", "sm")}
            >
              {saving ? "Submitting..." : "Submit"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/client-pools")}
              disabled={saving}
              className={btn("secondary", "sm")}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default AddClientPoolPage;
